import { ApolloLink, Observable } from "@apollo/client";
import { getPublicEnv } from "@tucan/utils";
import { getCookie } from "cookies-next";
import debug from "debug";
import { captureException } from '@tucan/utils/error-tracking'
import { getGraphQLUrl } from "./get.graphql.url";

const logger = debug("tokenLink");

/* ------------------------------------------------------------------ */
/* Refresh state (deduplicates concurrent refresh calls)               */
/* ------------------------------------------------------------------ */

let refreshPromise: Promise<void> | null = null;

/* ------------------------------------------------------------------ */
/* Token validity check (≈ isTokenValidOrUndefined)                     */
/* ------------------------------------------------------------------ */

async function shouldRefreshToken(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const expiresCookie = getCookie(`${getPublicEnv("COOKIE_PREFIX")}expires`);

  if (!expiresCookie) return false;

  // forced refresh flag
  if (localStorage.getItem("f")) return true;

  try {
    const expires = Number(expiresCookie);
    return expires < Date.now() / 1000 + 30;
  } catch {
    return true;
  }
}

/* ------------------------------------------------------------------ */
/* Refresh request (≈ fetchAccessToken + handleResponse)                */
/* ------------------------------------------------------------------ */

async function refreshAccessToken(): Promise<void> {
  const response = await fetch(getGraphQLUrl({ path: "/auth/refresh" }), {
    method: "POST",
    credentials: "include",
  });

  const result = await response.json();

  if (!response.ok || result?.error) {
    throw new Error(result?.error || "refresh failed");
  }

  onRefreshSuccess(result);
}

/* ------------------------------------------------------------------ */
/* Success handler (≈ handleFetch)                                      */
/* ------------------------------------------------------------------ */

function onRefreshSuccess(_: { loggedIn: boolean; expires: number }) {
  localStorage.removeItem("f");
}

/* ------------------------------------------------------------------ */
/* Error handler (≈ handleError)                                        */
/* ------------------------------------------------------------------ */

function onRefreshError(error: unknown) {
  logger("failed to refresh token %O", error);
  captureException(error);

  localStorage.removeItem("f");

  if (
    error instanceof Error &&
    error.message === "refresh token invalid" &&
    localStorage.getItem("refresh")
  ) {
    localStorage.removeItem(`${getPublicEnv("COOKIE_PREFIX")}expires`);
    window.location.reload();
  }
}

/* ------------------------------------------------------------------ */
/* Refresh orchestrator (shared, concurrency-safe)                      */
/* ------------------------------------------------------------------ */

async function ensureFreshToken(): Promise<void> {
  if (!(await shouldRefreshToken())) return;

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken()
      .catch((err) => {
        onRefreshError(err);
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/* ------------------------------------------------------------------ */
/* Apollo Client v4 native link                                         */
/* ------------------------------------------------------------------ */

export const tokenRefreshLink = new ApolloLink(
  (operation, forward) =>
    new Observable<ApolloLink.Result>((observer) => {
      let sub: { unsubscribe(): void } | undefined;

      ensureFreshToken()
        .then(() => {
          sub = forward(operation).subscribe({
            next: (value) => observer.next(value),
            complete: () => observer.complete(),
            error: async (err) => {
              const unauthenticated =
                err?.statusCode === 401 ||
                err?.networkError?.statusCode === 401 ||
                err?.result?.errors?.some(
                  (e: any) => e.extensions?.code === "UNAUTHENTICATED",
                );

              if (!unauthenticated) {
                observer.error(err);
                return;
              }

              try {
                await ensureFreshToken();

                sub = forward(operation).subscribe({
                  next: (v) => observer.next(v),
                  error: (e) => observer.error(e),
                  complete: () => observer.complete(),
                });
              } catch (refreshError) {
                observer.error(refreshError);
              }
            },
          });
        })
        .catch((err) => observer.error(err));

      return () => sub?.unsubscribe();
    }),
);
